import type { Interpolation } from '../../types';
import type { Asset } from '../asset/Container';
import type { Slide } from '../slide/Slide';

export type MorphableProperty = 'position.x' | 'position.y' | 'scale.x' | 'scale.y' | 'alpha';

type MorphSourceState = {
    asset: Asset;
    x: number;
    y: number;
    scaleX: number;
    scaleY: number;
    alpha: number;
};

export class MorphManager {
    #slide: Slide;
    #sourceStates: Map<string, MorphSourceState> = new Map();

    constructor(slide: Slide) {
        this.#slide = slide;
    }

    setupMorphAnimations() {
        const nextScene = this.#slide.nextScene;
        if (!nextScene) {
            return;
        }

        const sourceAssets = this.#slide
            .getAssets({ deep: true })
            .sort((a, b) => a.name.localeCompare(b.name));
        const targetAssets = nextScene
            .getAssets({ deep: true })
            .sort((a, b) => a.name.localeCompare(b.name));

        const claimedTargetIds = new Set<string>();

        sourceAssets.forEach((sourceAsset) => {
            const matchingAsset = targetAssets.find(
                (targetAsset) =>
                    !claimedTargetIds.has(targetAsset.id) && sourceAsset.isMorphMatch(targetAsset),
            );
            if (!matchingAsset) {
                return;
            }

            claimedTargetIds.add(matchingAsset.id);
            this.#setupMorphForAssetPair(sourceAsset, matchingAsset);
        });

        for (const { asset } of this.#sourceStates.values()) {
            if (!asset.hasMorphAnimation) {
                continue;
            }
            MorphManager.#forceBuildAndDraw(asset);
        }
    }

    clearMorphAnimations() {
        for (const state of this.#sourceStates.values()) {
            const { asset, x, y, scaleX, scaleY, alpha } = state;

            if (!asset || asset.destroyed) {
                continue;
            }

            asset.clearMorphAnimations();

            asset.transformation.position.x = x;
            asset.transformation.position.y = y;
            asset.scale.x = scaleX;
            asset.scale.y = scaleY;
            asset.alpha = alpha;

            MorphManager.#forceBuildAndDraw(asset);
        }
        this.#sourceStates.clear();
    }

    #setupMorphForAssetPair(sourceAsset: Asset, targetAsset: Asset) {
        const { duration: requestedDuration, interpolation } = sourceAsset.morphParameters;

        if (this.#slide.duration < requestedDuration) {
            return;
        }

        const morphDuration = requestedDuration;

        if (!this.#sourceStates.has(sourceAsset.id)) {
            this.#sourceStates.set(sourceAsset.id, {
                asset: sourceAsset,
                x: sourceAsset.transformation.position.x,
                y: sourceAsset.transformation.position.y,
                scaleX: sourceAsset.scale.x,
                scaleY: sourceAsset.scale.y,
                alpha: sourceAsset.alpha,
            });
        }

        MorphManager.#setupAnimations(sourceAsset, targetAsset, morphDuration, interpolation);
    }

    static #setupAnimations(
        sourceAsset: Asset,
        targetAsset: Asset,
        morphDuration: number,
        interpolation: Interpolation,
    ) {
        MorphManager.#forceBuildAndDraw(sourceAsset);
        MorphManager.#forceBuildAndDraw(targetAsset);

        const excludedProperties = sourceAsset.morphExcludedProperties;
        const animationConfigs = MorphManager.#buildAnimationConfigs(sourceAsset, targetAsset);

        Object.entries(animationConfigs).forEach(([morphableProperty, config]) => {
            if (excludedProperties.includes(morphableProperty as MorphableProperty)) {
                return;
            }

            const sourceAnim = sourceAsset.addMorphAnimation();
            sourceAnim.property = config.property;
            sourceAnim.startValue = config.startValue;
            sourceAnim.endValue = config.endValue;
            sourceAnim.duration = morphDuration;
            sourceAnim.interpolation = interpolation;
            sourceAnim.shouldOffsetPropertyValue = config.shouldOffsetPropertyValue;
        });
    }

    static #buildAnimationConfigs(sourceAsset: Asset, targetAsset: Asset) {
        const sourcePos = sourceAsset.transformation.position;
        const targetPos = targetAsset.transformation.position;
        const deltaX = targetPos.x - sourcePos.x;
        const deltaY = targetPos.y - sourcePos.y;

        const scaleRatioX = sourceAsset.width > 0 ? targetAsset.width / sourceAsset.width : 1;
        const scaleRatioY = sourceAsset.height > 0 ? targetAsset.height / sourceAsset.height : 1;

        const configs: Partial<
            Record<
                MorphableProperty,
                {
                    property: string;
                    shouldOffsetPropertyValue: boolean;
                    startValue: number;
                    endValue: number;
                }
            >
        > = {};

        if (deltaX !== 0) {
            configs['position.x'] = {
                property: 'transformation.position.x',
                shouldOffsetPropertyValue: true,
                startValue: 0,
                endValue: deltaX,
            };
        }

        if (deltaY !== 0) {
            configs['position.y'] = {
                property: 'transformation.position.y',
                shouldOffsetPropertyValue: true,
                startValue: 0,
                endValue: deltaY,
            };
        }

        if (scaleRatioX !== 1) {
            configs['scale.x'] = {
                property: 'scale.x',
                shouldOffsetPropertyValue: false,
                startValue: 1,
                endValue: scaleRatioX,
            };
        }

        if (scaleRatioY !== 1) {
            configs['scale.y'] = {
                property: 'scale.y',
                shouldOffsetPropertyValue: false,
                startValue: 1,
                endValue: scaleRatioY,
            };
        }

        if (sourceAsset.alpha !== targetAsset.alpha) {
            configs['alpha'] = {
                property: 'alpha',
                shouldOffsetPropertyValue: false,
                startValue: sourceAsset.alpha,
                endValue: targetAsset.alpha,
            };
        }

        return configs;
    }

    static #forceBuildAndDraw(asset: Asset) {
        const prevAutoDraw = asset.autoDraw;
        asset.autoDraw = true;
        asset.requiresRedraw = true;
        asset._buildAndDrawRecur();
        asset.autoDraw = prevAutoDraw;
    }
}
